import { FragmentOf, ResultOf } from "gql.tada";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { GetCaseStudyHomepageById } from "@queries/archetypes/GetCaseStudyHomepageById";
import { Hero } from "@components/entries/Hero";
import { RoutingSection } from "@components/entries/RoutingSection";
import { CaseStudyShowcase } from "@components/entries/CaseStudyShowcase";
import { CaseStudyInsightsBlockGroup } from "@components/entries/CaseStudyInsightsBlockGroup";
import { QuotationCarouselSection } from "@components/entries/QuotationCarouselSection";
import { CaseStudyIconGrid } from "@components/entries/CaseStudyIconGrid";
import { FootnoteProvider } from "@components/providers/FootnoteProvider";
import { FootnoteList } from "@components/shared/FootnoteList";
import { extractPageFootnotes } from "@utils/extractPageFootnotes";
import { BackgroundDeepContainer } from "@components/shared/BackgroundDeepContainer";
import { ScrollCueButton } from "@components/shared/ScrollCueButton";
import { NotFound } from "@components/shared/NotFound";

type Data = NonNullable<ResultOf<typeof GetCaseStudyHomepageById>["caseStudyHomepage"]>;

export const CaseStudyHomepageRenderer = ({ data }: { data: Data }) => {
  // Case study pages are only served in locales their content has been
  // explicitly localized for — a null localeGate means the requested locale
  // would merely fall back to en-US content, so we 404 instead.
  if (!data.localeGate) return <NotFound />;

  const footnoteEntries = extractPageFootnotes([
    data.hero?.bodyRichText,
    ...(data.caseStudyInsightsCollection?.items ?? []),
  ]);

  return (
    <FootnoteProvider footnoteEntries={footnoteEntries}>
      {data.hero && (
        <div className="relative">
          <Hero entry={{ hero: data.hero }} />
          <ScrollCueButton />
        </div>
      )}
      {data.caseStudyInsightsCollection?.items && data.caseStudyInsightsCollection.items.length > 0 && (
        <CaseStudyInsightsBlockGroup items={data.caseStudyInsightsCollection.items as (FragmentOf<typeof RichEntryFragment> | null)[]} />
      )}
      {data.caseStudyShowcase && <CaseStudyShowcase item={data.caseStudyShowcase} />}
      {data.highlightedQuotations && (
        <QuotationCarouselSection
          entry={{ quotationCarouselSection: data.highlightedQuotations }}
        />
      )}
      {data.additionalClientIcons && <CaseStudyIconGrid data={data.additionalClientIcons} />}
      {data.ctaFooter && (
        <BackgroundDeepContainer>
          <RoutingSection entry={{ routingSection: data.ctaFooter }} />
        </BackgroundDeepContainer>
      )}
      <FootnoteList />
    </FootnoteProvider>
  );
};
